import React from "react";
import {
  Users,
  Network,
  Database,
  Repeat,
  Banknote,
  ChefHat,
  Building2,
  Tent,
  GraduationCap,
  Landmark,
  Baby,
  CreditCard,
  ShieldCheck,
  Layers,
} from "lucide-react";

/* ============================================================
   MEGA MASTER DIAGRAM · MULTI-VERTICAL
   ------------------------------------------------------------
   The Many-to-One MoR picture:
     5 verticals → 1 MID → 1 Ledger → adjustments → 5 sub-ledgers
                 → batching cycle → 1 outbound net payout

   Pure SVG fan-in / fan-out, animated particles.
   Bilingual via t.megaFlow (FR + EN both shipped in i18n).
   ============================================================ */

const SRC_ICONS = {
  "ec-lunch":  ChefHat,
  "ec-kiddo":  Baby,
  "ec-school": GraduationCap,
  "ec-city":   Landmark,
  "ec-card":   CreditCard,
};

const SUB_ICONS = {
  "sl-trait":  ChefHat,
  "sl-gard":   Building2,
  "sl-camp":   Tent,
  "sl-school": GraduationCap,
  "sl-city":   Landmark,
};

/* X positions of the 5 columns (percentages). */
const X = {
  src: 8,
  mid: 28,
  led: 48,
  sub: 70,
  out: 92,
};

/* Y positions for the 5 stacked sources & sub-ledgers (percentages). */
const STACK_Y = [12, 30, 48, 66, 84];

const MegaMasterDiagram = ({ t }) => {
  const m = t.megaFlow;
  const sources = m.sources;
  const subs = m.subledgers;

  /* Build fan-in (sources → MID) and fan-out (ledger → sub-ledgers) paths. */
  const fanInPaths = sources.map((s, i) => ({
    id: `mf-in-${s.id}`,
    color: s.color,
    d: `M ${X.src} ${STACK_Y[i]} L ${X.mid} 48`,
  }));
  const midToLedger = `M ${X.mid} 48 L ${X.led} 48`;
  const fanOutPaths = subs.map((s, i) => ({
    id: `mf-out-${s.id}`,
    d: `M ${X.led} 48 L ${X.sub} ${STACK_Y[i]}`,
  }));
  const subToBatchPaths = subs.map((s, i) => ({
    id: `mf-batch-${s.id}`,
    d: `M ${X.sub} ${STACK_Y[i]} L ${X.out} 48`,
  }));

  return (
    <section
      id="mega-flow"
      className="ed-section ed-mega-section"
      data-testid="ed-mega-flow"
    >
      <header className="ed-section-head">
        <div className="ed-section-bar" style={{ background: "#bef264" }} />
        <h2 className="ed-section-title">{m.title}</h2>
        <p className="ed-section-subtitle">{m.subtitle}</p>
      </header>

      <div className="ed-mega-tag" data-testid="mega-tag">
        <ShieldCheck size={14} strokeWidth={2.4} />
        <span>{m.tag}</span>
      </div>

      {/* Column headers */}
      <div className="ed-mega-cols">
        <span className="ed-mega-col-tag" style={{ left: `${X.src}%` }}>{m.columns.sources}</span>
        <span className="ed-mega-col-tag" style={{ left: `${X.mid}%` }}>{m.columns.mid}</span>
        <span className="ed-mega-col-tag" style={{ left: `${X.led}%` }}>{m.columns.ledger}</span>
        <span className="ed-mega-col-tag" style={{ left: `${X.sub}%` }}>{m.columns.sub}</span>
        <span className="ed-mega-col-tag" style={{ left: `${X.out}%` }}>{m.columns.batch} · {m.columns.out}</span>
      </div>

      <div className="ed-mega-canvas" data-testid="mega-canvas">
        {/* SVG layer */}
        <svg
          className="ed-mega-svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="mf-grad-mid" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#bef264" stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id="mf-grad-out" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#bef264" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#f472b6" stopOpacity="0.85" />
            </linearGradient>
          </defs>

          {/* Fan-in: 5 sources → MID */}
          {fanInPaths.map((p, i) => (
            <g key={p.id}>
              <path
                d={p.d}
                stroke={p.color}
                strokeWidth="0.5"
                fill="none"
                opacity="0.75"
                strokeLinecap="round"
              />
              <circle r="0.9" fill={p.color}>
                <animateMotion dur={`${2.6 + i * 0.18}s`} repeatCount="indefinite" begin={`${i * 0.25}s`} path={p.d} />
              </circle>
            </g>
          ))}

          {/* MID → Ledger */}
          <path
            d={midToLedger}
            stroke="url(#mf-grad-mid)"
            strokeWidth="0.7"
            fill="none"
            opacity="0.95"
            strokeLinecap="round"
          />
          <circle r="1" fill="#bef264">
            <animateMotion dur="2.2s" repeatCount="indefinite" path={midToLedger} />
          </circle>

          {/* Ledger → 5 sub-ledgers (fan-out) */}
          {fanOutPaths.map((p, i) => (
            <g key={p.id}>
              <path
                d={p.d}
                stroke="url(#mf-grad-out)"
                strokeWidth="0.5"
                fill="none"
                opacity="0.75"
                strokeLinecap="round"
              />
              <circle r="0.85" fill="#f472b6">
                <animateMotion dur={`${2.4 + i * 0.18}s`} repeatCount="indefinite" begin={`${i * 0.25 + 0.5}s`} path={p.d} />
              </circle>
            </g>
          ))}

          {/* Sub-ledgers → Batch (dashed, slower — emphasizing periodic accumulation) */}
          {subToBatchPaths.map((p, i) => (
            <g key={p.id}>
              <path
                d={p.d}
                stroke="#f472b6"
                strokeWidth="0.35"
                fill="none"
                opacity="0.55"
                strokeDasharray="1.5 1.5"
                strokeLinecap="round"
              />
              <circle r="0.6" fill="#f472b6">
                <animateMotion dur={`${3.4 + i * 0.2}s`} repeatCount="indefinite" begin={`${i * 0.3 + 1.2}s`} path={p.d} />
              </circle>
            </g>
          ))}
        </svg>

        {/* Source nodes (5 verticals) */}
        {sources.map((s, i) => {
          const Icon = SRC_ICONS[s.id] || Users;
          return (
            <div
              key={s.id}
              className="ed-mega-node ed-mega-node-src"
              style={{ left: `${X.src}%`, top: `${STACK_Y[i]}%`, "--nc": s.color }}
              data-testid={`mega-src-${s.id}`}
            >
              {s.phase && (
                <span className="ed-mega-phase-badge" data-testid={`mega-src-phase-${s.id}`}>
                  {s.phase}
                </span>
              )}
              <div className="ed-mega-node-icon"><Icon size={16} strokeWidth={2.2} /></div>
              <div className="ed-mega-node-body">
                <strong className="ed-mega-node-label">{s.label}</strong>
                <span className="ed-mega-node-desc">{s.desc}</span>
              </div>
            </div>
          );
        })}

        {/* MID node — the single Merchant ID */}
        <div
          className="ed-mega-node ed-mega-node-mid"
          style={{ left: `${X.mid}%`, top: "48%" }}
          data-testid="mega-mid"
        >
          <div className="ed-mega-node-icon"><Network size={18} strokeWidth={2.4} /></div>
          <div className="ed-mega-node-body">
            <strong className="ed-mega-node-label">{m.mid.label}</strong>
            <span className="ed-mega-node-desc">{m.mid.desc}</span>
          </div>
        </div>

        {/* Ledger node — the single source of truth */}
        <div
          className="ed-mega-node ed-mega-node-ledger"
          style={{ left: `${X.led}%`, top: "48%" }}
          data-testid="mega-ledger"
        >
          <div className="ed-mega-node-icon"><Database size={18} strokeWidth={2.4} /></div>
          <div className="ed-mega-node-body">
            <strong className="ed-mega-node-label">{m.ledger.label}</strong>
            <span className="ed-mega-node-desc">{m.ledger.desc}</span>
          </div>

          {/* Adjustments inline pills */}
          <div className="ed-mega-adj-pills" data-testid="mega-adjustments">
            <span className="ed-mega-adj-title">{m.columns.adjust}</span>
            {m.adjustments.map((adj) => (
              <span key={adj} className="ed-mega-adj-pill">{adj}</span>
            ))}
          </div>
        </div>

        {/* Sub-ledger nodes (5) */}
        {subs.map((s, i) => {
          const Icon = SUB_ICONS[s.id] || Layers;
          return (
            <div
              key={s.id}
              className="ed-mega-node ed-mega-node-sub"
              style={{ left: `${X.sub}%`, top: `${STACK_Y[i]}%` }}
              data-testid={`mega-sub-${s.id}`}
            >
              <div className="ed-mega-node-icon"><Icon size={15} strokeWidth={2.2} /></div>
              <div className="ed-mega-node-body">
                <strong className="ed-mega-node-label">{s.label}</strong>
              </div>
            </div>
          );
        })}

        {/* Batch + outbound payout combined node */}
        <div
          className="ed-mega-node ed-mega-node-batch"
          style={{ left: `${X.out}%`, top: "48%" }}
          data-testid="mega-batch"
        >
          <div className="ed-mega-node-icon"><Repeat size={16} strokeWidth={2.4} /></div>
          <div className="ed-mega-node-body">
            <strong className="ed-mega-node-label">{m.batch.label}</strong>
            <span className="ed-mega-node-desc">{m.batch.desc}</span>
          </div>
          <div className="ed-mega-payout-tag">
            <Banknote size={12} strokeWidth={2.4} />
            <span>{m.payout.label}</span>
          </div>
          <span className="ed-mega-payout-desc">{m.payout.desc}</span>
        </div>
      </div>

      {/* Key facts list */}
      <ul className="ed-mega-facts" data-testid="mega-facts">
        {m.keyFacts.map((fact, i) => (
          <li key={i} className="ed-mega-fact">
            <ShieldCheck size={14} strokeWidth={2.4} />
            <span>{fact}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default MegaMasterDiagram;
